/**
 * AppointmentTileMeta — compact meta row for calendar GRID tiles
 * (By-Doctor, By-Room, Week). Surfaces two things Flutter's tile shows
 * that the React grid tiles previously omitted (PROD-990, gap #11):
 *
 *   1. Patient-importance badge (code + color) — Flutter
 *      `calendar_tab.dart:2017-2044` renders `patient.importance.code`
 *      in an `IndicatorTagWidget` coloured by
 *      `ColorHelper.getImportanceColor(code)` with a tooltip of the
 *      importance display. That helper maps VIP → lemon-green, everything
 *      else → brand blue. We mirror that exactly. VIP-green is already the
 *      sanctioned repo convention for importance (see PatientAvatar's
 *      `ring-green-500`), so it does NOT breach the clinical-only-green
 *      rule — importance IS the semantic.
 *   2. Location icon (office / home / virtual) + tooltip — Flutter
 *      `calendar_event_card_widget.dart:406-414` renders a `LocationImage`
 *      keyed on the location name, tinted by the profile colour, wrapped
 *      in a `CircularTooltip(location.name)`. We reuse the same ScripsIcon
 *      glyphs + the appointment's location name as the title tooltip.
 *
 * The day-view *table* already surfaces both (PatientAvatar importance ring
 * + LeadingLocationIcon); this brings the compact grid tiles to parity.
 */
import { ScripsIcon, type ScripsIconName } from '@/shared/ui/components';
import { categoryColor } from '@/shared/lib/category-color';
import { cn } from '@/lib/utils';

/** location name (lower-cased) → Scrips glyph. Mirrors day-list LOCATION_ICONS. */
const LOCATION_ICONS: Record<string, ScripsIconName> = {
  office: 'office',
  home: 'home',
  virtual: 'virtual',
  block: 'block',
};

function locationIconName(location?: string): ScripsIconName {
  // Empty/unknown falls through to 'office' (Flutter IconHelper default).
  return !location ? 'office' : LOCATION_ICONS[location.toLowerCase()] ?? 'office';
}

export interface AppointmentTileMetaProps {
  /** Location CATEGORY that selects the glyph (office/home/virtual/block). */
  location?: string;
  /** Human location name for the tooltip (falls back to `location`). */
  locationName?: string;
  /** Profile colour (CSS) tinting the location glyph, per Flutter. */
  profileColor?: string;
  /** Patient importance CODE (e.g. 'VIP'). Badge hidden when absent. */
  importance?: string;
  /** Importance display for the badge tooltip (falls back to the code). */
  importanceDisplay?: string;
  className?: string;
}

/** True for the VIP importance code — the one Flutter tints lemon-green. */
function isVip(code: string): boolean {
  return code.toUpperCase() === 'VIP';
}

export function AppointmentTileMeta({
  location,
  locationName,
  profileColor,
  importance,
  importanceDisplay,
  className,
}: AppointmentTileMetaProps) {
  const code = importance?.trim();
  const showImportance = Boolean(code);
  return (
    <span className={cn('flex items-center gap-1 flex-shrink-0', className)}>
      {showImportance ? (
        <span
          title={importanceDisplay?.trim() || code}
          className={cn(
            'rounded px-1 text-size-xs font-bold leading-[1.4] text-white uppercase',
            // VIP → green (Flutter lemon-green); any other importance → brand blue.
            isVip(code!) ? 'bg-green-500' : 'bg-brand-primary',
          )}
        >
          {code}
        </span>
      ) : null}
      <span
        title={locationName?.trim() || location || 'Office'}
        className="inline-flex items-center"
      >
        <ScripsIcon
          name={locationIconName(location)}
          size={12}
          aria-label={locationName?.trim() || location || 'Office'}
          style={profileColor ? { color: categoryColor(profileColor, 600) } : undefined}
        />
      </span>
    </span>
  );
}
